import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MouseIcon from '@mui/icons-material/Mouse';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import GetAppIcon from '@mui/icons-material/GetApp';
import CreateIcon from '@mui/icons-material/Create';
import { useNavigate } from 'react-router-dom';

import { PageHeader, RsocNavHeader } from './';
import MainLayoutDemand from '../MainLoyout/MainLayoutDemand';
import Api from '../../Services/Api';
import KeywordSuggestions from './KeywordSuggestions';

const RsocDiscovery = () => {
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [terms, setTerms] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [orderBy, setOrderBy] = useState('epc');
  const [order, setOrder] = useState('desc');
  
  // Initialize dates properly - ensure we're not going into the future
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day
  
  const sevenDaysAgo = subDays(today, 7);
  sevenDaysAgo.setHours(0, 0, 0, 0); // Set to start of day
  
  const [startDate, setStartDate] = useState(sevenDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [minClicks, setMinClicks] = useState(10); // Changed back to 10 as default
  const [minEpc, setMinEpc] = useState(0.001); // Lowered from 0.01 to 0.001
  const [searchTerm, setSearchTerm] = useState('');
  
  // Validate dates when they change
  const handleStartDateChange = (newDate) => {
    if (newDate && newDate instanceof Date && !isNaN(newDate)) {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      const validDate = newDate > now ? now : newDate;
      validDate.setHours(0, 0, 0, 0);
      setStartDate(validDate);
    }
  };
  
  const handleEndDateChange = (newDate) => {
    if (newDate && newDate instanceof Date && !isNaN(newDate)) {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      const validDate = newDate > now ? now : newDate;
      validDate.setHours(23, 59, 59, 999);
      setEndDate(validDate);
    }
  };
  
  // Summary metrics
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalTerms: 0,
    avgEpc: 0,
    totalRevenue: 0,
    totalClicks: 0,
    highPerformers: 0
  });
  
  // High performing terms for AI suggestions
  const [highPerformingTerms, setHighPerformingTerms] = useState([]);

  // Fetch performance data
  const fetchPerformanceData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate dates before formatting
      if (!startDate || !endDate) {
        setError('Please select valid start and end dates');
        setLoading(false);
        return;
      }
      
      // Ensure dates are not in the future
      const now = new Date();
      const validStartDate = startDate > now ? now : startDate;
      const validEndDate = endDate > now ? now : endDate;
      
      // Format dates as YYYY-MM-DD for parking events endpoint
      const formattedStartDate = format(validStartDate, 'yyyy-MM-dd');
      const formattedEndDate = format(validEndDate, 'yyyy-MM-dd');
      
      console.log('Date validation:', {
        originalStartDate: startDate,
        originalEndDate: endDate,
        validStartDate: validStartDate,
        validEndDate: validEndDate,
        formattedStartDate: formattedStartDate,
        formattedEndDate: formattedEndDate
      });
      
      // Request parking events data grouped by ref_keyword (terms)
      const requestParams = {
        order_by: orderBy,
        sort_order: order,
        page: String(page + 1),
        per_page: String(rowsPerPage),
        filter: {
          date_range: [formattedStartDate, formattedEndDate]
        }
      };
      
      // Add API-level filters
      if (minClicks > 0) {
        requestParams.min_clicks = minClicks;
      }
      if (minEpc > 0.001) {
        requestParams.min_epc = minEpc;
      }
      if (searchTerm.trim() !== '') {
        requestParams.search_term = searchTerm.trim();
      }
      
      console.log('Parking Events API Request:', requestParams);
      
      const response = await Api.getPerformanceReport(requestParams);
      
      console.log('Parking Events API Response:', response);
      
      if (response && response.metrics) {
        const data = response.metrics.data || [];
        
        // Since API handles filtering, we use the data as-is
        const processedData = data;
        
        console.log('Data processing:', {
          rawDataCount: data.length,
          processedDataCount: processedData.length,
          apiFilters: { minClicks, minEpc, searchTerm },
          sampleRawData: data.slice(0, 3),
          sampleProcessedData: processedData.slice(0, 3)
        });
        
        // Calculate summary metrics
        const totalRevenue = processedData.reduce((sum, term) => sum + (parseFloat(term.credited_revenue) || 0), 0);
        const totalClicks = processedData.reduce((sum, term) => sum + (parseInt(term.clicks) || 0), 0);
        const avgEpc = totalClicks > 0 ? totalRevenue / totalClicks : 0;
        const highPerformers = processedData.filter(term => (parseFloat(term.epc) || 0) >= 0.8).length;
        
        // Extract high-performing terms for AI suggestions
        const highPerformingTermsList = processedData
          .filter(term => (parseFloat(term.epc) || 0) >= 0.8)
          .map(term => term.term)
          .slice(0, 25); // Limit to top 25 for better AI processing
        
        setSummaryMetrics({
          totalTerms: processedData.length,
          avgEpc: avgEpc,
          totalRevenue: totalRevenue,
          totalClicks: totalClicks,
          highPerformers: highPerformers
        });
        
        setTerms(processedData);
        setTotalItems(response.metrics.total || processedData.length);
        setHighPerformingTerms(highPerformingTermsList);
        
        console.log('High performing terms extracted:', {
          count: highPerformingTermsList.length,
          terms: highPerformingTermsList
        });
      }
    } catch (err) {
      console.error('Error fetching performance data:', err);
      setError('Failed to fetch performance data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = () => {
    setPage(0);
    fetchPerformanceData();
  };

  // Handle sort
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Generate content for term
  const handleGenerateContent = (term) => {
    // Navigate to generator with pre-filled term
    navigate('/rsoc/generator', { 
      state: { 
        prefilledKeywords: [term.term],
        prefilledTitle: `Best ${term.term} Guide`
      }
    });
  };

  // Export to CSV using current table data
  const handleExportCSV = () => {
    try {
      if (terms.length === 0) {
        setError('No data to export');
        return;
      }

      // Create CSV header
      const headers = ['Term', 'Clicks', 'EPC', 'Credited Revenue'];
      
      // Create CSV rows from current table data
      const csvRows = [
        headers.join(','),
        ...terms.map(term => {
          const row = [
            `"${term.term.replace(/"/g, '""')}"`, // Escape quotes in term
            term.clicks,
            term.epc.toFixed(3),
            term.credited_revenue.toFixed(2)
          ];
          return row.join(',');
        })
      ];
      
      // Create CSV string
      const csvContent = csvRows.join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rsoc_discovery_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Show success message (optional)
      console.log('CSV exported successfully');
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError('Failed to export CSV. Please try again.');
    }
  };

  // Initial load
  useEffect(() => {
    console.log('Initial component load - Date states:', {
      startDate: startDate,
      startDateString: startDate ? startDate.toString() : 'null',
      startDateFormatted: startDate ? format(startDate, 'yyyy-MM-dd') : 'null',
      endDate: endDate,
      endDateString: endDate ? endDate.toString() : 'null',
      endDateFormatted: endDate ? format(endDate, 'yyyy-MM-dd') : 'null',
      currentDate: new Date(),
      currentDateString: new Date().toString()
    });
    fetchPerformanceData();
  }, [page, rowsPerPage, orderBy, order, minClicks, minEpc, searchTerm]);

  return (
    <MainLayoutDemand>
      <Box sx={{ mb: 3 }}>
        <PageHeader title="Term Discovery & Analysis" />
        <RsocNavHeader />
      </Box>

      {/* Filters Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
          Discovery Filters
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                maxDate={new Date()}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                maxDate={new Date()}
                minDate={startDate}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              label="Min Clicks"
              type="number"
              value={minClicks}
              onChange={(e) => setMinClicks(parseInt(e.target.value) || 0)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start"><MouseIcon fontSize="small" /></InputAdornment>
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              label="Min EPC ($)"
              type="number"
              value={minEpc}
              onChange={(e) => setMinEpc(parseFloat(e.target.value) || 0)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                inputProps: { step: 0.01 }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              label="Search Terms"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              fullWidth
              placeholder="Filter by term..."
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              fullWidth
              startIcon={<SearchIcon />}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', boxShadow: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Terms
              </Typography>
              <Typography variant="h5" component="div">
                {loading ? <Skeleton /> : summaryMetrics.totalTerms.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', boxShadow: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Avg EPC
              </Typography>
              <Typography variant="h5" component="div">
                {loading ? <Skeleton /> : `$${summaryMetrics.avgEpc.toFixed(3)}`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', boxShadow: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Revenue
              </Typography>
              <Typography variant="h5" component="div">
                {loading ? <Skeleton /> : `$${summaryMetrics.totalRevenue.toFixed(2)}`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', boxShadow: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                Total Clicks
              </Typography>
              <Typography variant="h5" component="div">
                {loading ? <Skeleton /> : summaryMetrics.totalClicks.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ height: '100%', boxShadow: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="caption">
                High Performers
              </Typography>
              <Typography variant="h5" component="div" color="success.main">
                {loading ? <Skeleton /> : summaryMetrics.highPerformers}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                EPC ≥ $0.80
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Results Table */}
      <Paper sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Discovered Terms</Typography>
          <Box>
            <IconButton onClick={() => fetchPerformanceData()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
            <Button
              startIcon={<GetAppIcon />}
              onClick={handleExportCSV}
              disabled={loading}
              sx={{ ml: 1 }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'term'}
                    direction={orderBy === 'term' ? order : 'asc'}
                    onClick={() => handleRequestSort('term')}
                  >
                    Term
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'clicks'}
                    direction={orderBy === 'clicks' ? order : 'asc'}
                    onClick={() => handleRequestSort('clicks')}
                  >
                    Clicks
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'epc'}
                    direction={orderBy === 'epc' ? order : 'asc'}
                    onClick={() => handleRequestSort('epc')}
                  >
                    EPC
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'credited_revenue'}
                    direction={orderBy === 'credited_revenue' ? order : 'asc'}
                    onClick={() => handleRequestSort('credited_revenue')}
                  >
                    Revenue
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Performance</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))
              ) : terms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      No terms found matching your criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                terms.map((term, index) => {
                  const epc = parseFloat(term.epc) || 0;
                  const isHighPerformer = epc >= 0.8;
                  const isGoodPerformer = epc >= 0.25 && epc < 0.8;
                  
                  return (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {term.term}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {parseInt(term.clicks || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={isHighPerformer ? 'success.main' : isGoodPerformer ? 'warning.main' : 'text.primary'}
                          fontWeight={isHighPerformer ? 600 : 400}
                        >
                          ${epc.toFixed(3)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        ${parseFloat(term.credited_revenue || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        {isHighPerformer ? (
                          <Chip label="High" color="success" size="small" icon={<TrendingUpIcon />} />
                        ) : isGoodPerformer ? (
                          <Chip label="Good" color="warning" size="small" />
                        ) : (
                          <Chip label="Average" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Generate RSOC Page">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleGenerateContent(term)}
                          >
                            <CreateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* AI-Generated Keyword Suggestions */}
      <KeywordSuggestions highPerformingTerms={highPerformingTerms} />
    </MainLayoutDemand>
  );
};

export default RsocDiscovery; 